/**
 * Agent Download Page.
 *
 * Provides OS-specific download buttons, install script,
 * and post-installation instructions for the atlas-agent binary.
 *
 * Sprint: 73, Task: S73-B01
 */

import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '@/i18n';
import {
  Card,
  Spinner,
} from '@/components/ui';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  useLatestAgentVersion,
  useAgentRelease,
  getInstallScriptUrl,
} from '@/features/mcp/hooks/useAgentDownloads';
import type { AgentBinaryInfo, AgentOS } from '@/features/mcp/agent-download-types';
import { OS_LABELS, ARCH_LABELS, LINUX_ARCH_LABELS } from '@/features/mcp/agent-download-types';
import {
  Download,
  Terminal,
  Copy,
  Check,
  ArrowRight,
  Apple,
  Monitor,
  Shield,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';

// =============================================================================
// Helpers
// =============================================================================

function detectUserOS(): AgentOS {
  const ua = navigator.userAgent.toLowerCase();
  if (ua.includes('mac') || ua.includes('darwin')) return 'darwin';
  if (ua.includes('win')) return 'windows';
  return 'linux';
}

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '—';
  const mb = bytes / (1024 * 1024);
  return `${mb.toFixed(1)} MB`;
}

function getArchLabel(os: AgentOS, arch: string): string {
  if (os === 'linux') return LINUX_ARCH_LABELS[arch] ?? arch;
  return ARCH_LABELS[arch] ?? arch;
}

function buildInstallCommand(apiBaseUrl: string, installScriptUrl: string): string {
  return `curl -fsSL ${apiBaseUrl}${installScriptUrl} | sh`;
}

function buildEnrollCommand(apiBaseUrl: string, enrollmentKey: string): string {
  // Add --insecure for dev-local environments that use self-signed CA certificates
  // (any .local domain, localhost, or RFC-1918 private IP ranges)
  let isDevLocal = false;
  try {
    const url = new URL(apiBaseUrl);
    isDevLocal =
      url.hostname.endsWith('.local') ||
      url.hostname === 'localhost' ||
      url.hostname === '127.0.0.1' ||
      url.hostname.startsWith('192.168.') ||
      url.hostname.startsWith('10.') ||
      url.hostname.startsWith('172.');
  } catch {
    // If URL parsing fails, default to no insecure flag
  }
  const insecureFlag = isDevLocal ? ' --insecure' : '';
  return `atlas-agent enroll --key ${enrollmentKey} --url ${apiBaseUrl}${insecureFlag}`;
}

/** Track binary download for analytics. */
function trackDownload(os: string, arch: string, version: string): void {
  const payload = { os, arch, version, timestamp: new Date().toISOString() };
  // Best-effort POST to API tracking endpoint (fire-and-forget)
  fetch('/api/v1/mcp/agents/downloads/track', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  }).catch(() => {
    // Silently ignore tracking failures — they must not block downloads
  });
  // Also log to console for local dev visibility
  console.info('[atlas-agent] download tracked', payload);
}

function getOSIcon(os: AgentOS) {
  switch (os) {
    case 'darwin':
      return <Apple className="w-5 h-5" />;
    case 'windows':
      return <Monitor className="w-5 h-5" />;
    default:
      return <Terminal className="w-5 h-5" />;
  }
}

// =============================================================================
// Sub-components
// =============================================================================

function CopyableCode({ code, label }: { code: string; label?: string }) {
  const { t } = useI18n();
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div>
      {label && (
        <span className="text-xs text-muted-foreground mb-1 block">{label}</span>
      )}
      <div className="relative">
        <pre className="bg-muted/50 rounded-lg p-3 pr-10 text-xs font-mono overflow-x-auto whitespace-pre-wrap break-all">
          {code}
        </pre>
        <button
          onClick={handleCopy}
          className="absolute top-2 right-2 text-muted-foreground hover:text-foreground transition-colors"
          title={t('admin.agentDownload.copyToClipboard')}
        >
          {copied ? (
            <Check className="w-4 h-4 text-green-500" />
          ) : (
            <Copy className="w-4 h-4" />
          )}
        </button>
      </div>
    </div>
  );
}

function BinaryDownloadCard({
  binary,
  os,
  version,
}: {
  binary: AgentBinaryInfo;
  os: AgentOS;
  version: string;
}) {
  const { t } = useI18n();
  const archLabel = getArchLabel(os, binary.arch);

  return (
    <Card className="p-4 flex flex-col items-center text-center gap-3 hover:border-primary/50 transition-colors">
      <Download className="w-8 h-8 text-primary" />
      <div>
        <p className="font-medium text-sm">{archLabel}</p>
        <p className="text-xs text-muted-foreground mt-0.5">
          v{version} · {formatFileSize(binary.size_bytes)}
        </p>
      </div>
      <a
        href={binary.download_url}
        onClick={() => trackDownload(os, binary.arch, version)}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
      >
        <Download className="w-4 h-4" />
        {t('admin.agentDownload.download')}
      </a>
    </Card>
  );
}

function PostInstallInstructions({
  os,
  apiBaseUrl,
}: {
  os: AgentOS;
  apiBaseUrl: string;
}) {
  const { t } = useI18n();
  const steps: { title: string; code?: string }[] = useMemo(() => {
    const enrollCommand = buildEnrollCommand(apiBaseUrl, 'EK_xxx');

    switch (os) {
      case 'darwin':
        return [
          {
            title: t('admin.agentDownload.steps.darwin.moveToBin'),
            code: 'chmod +x atlas-agent-darwin-*\nmv atlas-agent-darwin-* ~/.local/bin/atlas-agent',
          },
          {
            title: t('admin.agentDownload.steps.darwin.quarantine'),
            code: 'xattr -d com.apple.quarantine ~/.local/bin/atlas-agent',
          },
          {
            title: t('admin.agentDownload.steps.darwin.enroll'),
            code: enrollCommand,
          },
          {
            title: t('admin.agentDownload.steps.darwin.connect'),
            code: 'atlas-agent connect',
          },
          {
            title: t('admin.agentDownload.steps.darwin.service'),
            code: 'atlas-agent service install\natlas-agent service start',
          },
        ];
      case 'linux':
        return [
          {
            title: t('admin.agentDownload.steps.linux.moveToBin'),
            code: 'chmod +x atlas-agent-linux-*\nsudo mv atlas-agent-linux-* /usr/local/bin/atlas-agent',
          },
          {
            title: t('admin.agentDownload.steps.linux.enroll'),
            code: enrollCommand,
          },
          {
            title: t('admin.agentDownload.steps.linux.connect'),
            code: 'atlas-agent connect',
          },
          {
            title: t('admin.agentDownload.steps.linux.service'),
            code: 'sudo atlas-agent service install\nsudo atlas-agent service start',
          },
        ];
      case 'windows':
        return [
          {
            title: t('admin.agentDownload.steps.windows.moveToBin'),
            code: 'move atlas-agent-windows-amd64.exe %USERPROFILE%\\AppData\\Local\\bin\\atlas-agent.exe',
          },
          {
            title: t('admin.agentDownload.steps.windows.enroll'),
            code: enrollCommand,
          },
          {
            title: t('admin.agentDownload.steps.windows.connect'),
            code: 'atlas-agent connect',
          },
          {
            title: t('admin.agentDownload.steps.windows.service'),
            code: 'atlas-agent.exe service install\natlas-agent.exe service start',
          },
        ];
      default:
        return [];
    }
  }, [apiBaseUrl, os, t]);

  return (
    <div className="space-y-4">
      <h3 className="font-semibold text-sm">{t('admin.agentDownload.postInstall')}</h3>
      <ol className="space-y-3">
        {steps.map((step, i) => (
          <li key={i} className="text-sm">
            <p className="text-muted-foreground mb-1">
              {i + 1}. {step.title}
            </p>
            {step.code && <CopyableCode code={step.code} />}
          </li>
        ))}
      </ol>
    </div>
  );
}

// =============================================================================
// Main Component
// =============================================================================

export function AgentDownloadPage() {
  const { t } = useI18n();
  const defaultOS = detectUserOS();
  const [selectedOS, setSelectedOS] = useState<AgentOS>(defaultOS);
  const [showChecksums, setShowChecksums] = useState(false);

  const { data: latestVersion, isLoading: loadingVersion } = useLatestAgentVersion();
  const version = latestVersion?.version ?? '';
  const { data: release, isLoading: loadingRelease } = useAgentRelease(version);

  const isLoading = loadingVersion || loadingRelease;

  // Filter binaries for the selected OS
  const osBinaries = useMemo(() => {
    if (!release?.binaries) return [];
    return release.binaries.filter((b) => b.os === selectedOS);
  }, [release, selectedOS]);

  const installScriptUrl = getInstallScriptUrl();
  const apiBaseUrl = window.location.origin;
  const installCommand = buildInstallCommand(apiBaseUrl, installScriptUrl);

  return (
    <div className="space-y-6 max-w-3xl">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Download className="w-6 h-6 text-primary" />
          {t('admin.agentDownload.title')}
        </h1>
        <p className="text-muted-foreground mt-1">
          {t('admin.agentDownload.subtitle')}
        </p>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Spinner />
        </div>
      ) : !version ? (
        <Card className="p-12 text-center">
          <Download className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
          <p className="text-muted-foreground">
            {t('admin.agentDownload.noReleases')}
          </p>
        </Card>
      ) : (
        <>
          {/* OS Tabs */}
          <Tabs
            value={selectedOS}
            onValueChange={(v) => setSelectedOS(v as AgentOS)}
          >
            <TabsList className="w-full justify-start">
              {(['darwin', 'linux', 'windows'] as AgentOS[]).map((os) => (
                <TabsTrigger
                  key={os}
                  value={os}
                  className="flex items-center gap-2"
                >
                  {getOSIcon(os)}
                  {OS_LABELS[os]}
                </TabsTrigger>
              ))}
            </TabsList>

            {(['darwin', 'linux', 'windows'] as AgentOS[]).map((os) => (
              <TabsContent key={os} value={os} className="space-y-6">
                {/* Download Buttons */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {osBinaries.length > 0 ? (
                    osBinaries.map((binary) => (
                      <BinaryDownloadCard
                        key={binary.binary_name}
                        binary={binary}
                        os={os}
                        version={version}
                      />
                    ))
                  ) : (
                    <Card className="col-span-2 p-6 text-center text-muted-foreground">
                      {t('admin.agentDownload.noBinaries', { os: OS_LABELS[os] })}
                    </Card>
                  )}
                </div>

                {/* Install Script */}
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground text-center">
                    {t('admin.agentDownload.installViaCli')}
                  </p>
                  <CopyableCode code={installCommand} />
                </div>

                {/* Post-install */}
                <Card className="p-5">
                  <PostInstallInstructions os={os} apiBaseUrl={apiBaseUrl} />
                </Card>
              </TabsContent>
            ))}
          </Tabs>

          {/* Checksums */}
          <Card className="p-4">
            <button
              onClick={() => setShowChecksums(!showChecksums)}
              className="flex items-center gap-2 text-sm font-medium w-full text-left"
            >
              <Shield className="w-4 h-4 text-primary" />
              {t('admin.agentDownload.checksums')}
              {showChecksums ? (
                <ChevronDown className="w-4 h-4 ml-auto" />
              ) : (
                <ChevronRight className="w-4 h-4 ml-auto" />
              )}
            </button>
            {showChecksums && release?.has_checksums && (
              <div className="mt-3">
                <CopyableCode
                  code={release.binaries
                    .map((b) => `${b.sha256}  ${b.binary_name}`)
                    .join('\n')}
                  label={t('admin.agentDownload.checksumsLabel')}
                />
                <a
                  href={`/api/v1/agent-downloads/${version}/checksums.txt`}
                  className="text-xs text-primary hover:underline mt-2 inline-block"
                >
                  {t('admin.agentDownload.downloadChecksums')}
                </a>
              </div>
            )}
          </Card>

          {/* Link to Runners */}
          <div className="flex justify-end">
            <Link
              to="/integrations/runners"
              className="text-sm text-primary hover:underline flex items-center gap-1"
            >
              {t('admin.agentDownload.manageRunners')}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </>
      )}
    </div>
  );
}
